import {withStylesProvider} from '../../utils/withStylesProvider';

import {WidgetBody} from './Body';
import {WidgetContainer} from './Container';
import {WidgetEmpty} from './Empty';
import {WidgetError} from './Error';
import {WidgetFooter} from './Footer';
import {WidgetHeader} from './Header';
import {WidgetSkeleton} from './Skeleton';
import {StylesProvider} from './context';
import {widgetTheme} from './theme/theme';

type WidgetType = typeof WidgetContainer & {
  Container: typeof WidgetContainer;
  Footer: typeof WidgetFooter;
  Header: typeof WidgetHeader;
  Body: typeof WidgetBody;
  Empty: typeof WidgetEmpty;
  Error: typeof WidgetError;
  Skeleton: typeof WidgetSkeleton;
};

/**
 * Widget component.
 * A compound component that provides a layout & base styles for widgets.
 * @example - Basic usage
 * ```tsx
 * <Widget>
 *  <Widget.Header title="Widget title" />
 * <Widget.Body>
 *  <Text>Widget content</Text>
 * </Widget.Body>
 * </Widget>
 * ```
 * @example - With Error
 * ```tsx
 * <Widget hasError={true}>
 *  <Widget.Header title="Widget title" />
 * <Widget.Body>
 *  <Text>Widget content</Text>
 * </Widget.Body>
 * <Widget.Error img={<Image src={'my-error.png'} />} title={`Something went wrong`} />
 * </Widget>
 * ```
 * @example - With Empty
 * ```tsx
 * <Widget hasError={true}>
 *  <Widget.Header title="Widget title" />
 * <Widget.Body>
 *  <Text>Widget content</Text>
 * </Widget.Body>
 * <Widget.Empty img={<Image src={'cute-void.png'} />} title={`Nothing here!`} />
 * </Widget>
 * ```
 */
const Widget = withStylesProvider(WidgetContainer, StylesProvider, widgetTheme) as unknown as WidgetType;
Widget.Container = WidgetContainer;
Widget.Footer = WidgetFooter;
Widget.Header = WidgetHeader;
Widget.Body = WidgetBody;
Widget.Empty = WidgetEmpty;
Widget.Error = WidgetError;
Widget.Skeleton = WidgetSkeleton;

// We do names export as well as dot-notation since next.js RSC doesn't support dot-notation pattern.
export {Widget, WidgetContainer, WidgetFooter, WidgetHeader, WidgetEmpty, WidgetBody};
export type {WidgetProps} from './Container';
export type {WidgetBodyProps} from './Body';
